/**
 * Reservoir referral status page.
 *
 * Route: /i/:code
 *
 * Pulls status from GET /api/waitlist/v2/status/:code and renders:
 *   - Position card (rank + estimated wait)
 *   - Progress bar to next tier
 *   - Personal share link + copy / X / LinkedIn / WhatsApp share buttons
 *   - Tier ladder
 *
 * Visuals match the Reservoir design system exactly:
 *   - Manrope display + IBM Plex Sans body
 *   - #fafaf7 cream, #0a0a0a ink, 14px radius cards
 *   - Tabular numerics on every number
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowUpRight, Check, Copy, Linkedin, Share2, Twitter, MessageCircle,
} from "lucide-react";

const TIER_LADDER = [
  { min_refs: 0,  name: "On the list",      code: "list" },
  { min_refs: 3,  name: "Skip the line",    code: "skip" },
  { min_refs: 10, name: "Founding Member",  code: "founding" },
  { min_refs: 25, name: "Reservoir 25",     code: "reservoir25" },
];

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

export default function ReferralStatus() {
  const { code } = useParams();
  const [data, setData]     = useState(null);
  const [err,  setErr]      = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${BACKEND}/api/waitlist/v2/status/${encodeURIComponent(code)}`);
        if (!r.ok) throw new Error(r.status === 404 ? "Link not found" : "Failed to load");
        const j = await r.json();
        if (alive) setData(j);
      } catch (e) {
        if (alive) setErr(String(e.message || e));
      }
    })();
    return () => { alive = false; };
  }, [code]);

  const shareUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    return `${window.location.origin}/?r=${encodeURIComponent(code || "")}`;
  }, [code]);

  if (err) {
    return (
      <Shell>
        <Card>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">Link not found.</h1>
          <p className="text-neutral-700 text-[14px] mt-3 leading-relaxed">
            That referral link doesn't match anyone in our waitlist. Double-check the URL, or
            <Link to="/" className="ml-1 underline underline-offset-2 text-black">request an invite here</Link>.
          </p>
        </Card>
      </Shell>
    );
  }

  if (!data) {
    return (
      <Shell>
        <div className="flex justify-center py-20">
          <span className="inline-block w-5 h-5 rounded-full border-2 border-black/15 border-t-black animate-spin" />
        </div>
      </Shell>
    );
  }

  const { position, total_waitlist, referrals, estimated_weeks, tier, next_tier, is_founding } = data;

  const progress = next_tier
    ? Math.min(1, referrals / next_tier.min_refs)
    : 1;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* ignore */ }
  };

  const shareText = encodeURIComponent(
    "I just joined the Reservoir AI waitlist — the AI concierge for points, miles, and credit-card rewards. Join with my link:"
  );
  const enc = encodeURIComponent(shareUrl);
  const tweet    = `https://twitter.com/intent/tweet?text=${shareText}&url=${enc}`;
  const linkedin = `https://www.linkedin.com/sharing/share-offsite/?url=${enc}`;
  const whatsapp = `https://wa.me/?text=${shareText}%20${enc}`;

  return (
    <Shell>
      {/* Headline */}
      <div className="text-center" data-testid="referral-status-header">
        <div className="section-eyebrow">You're on the list</div>
        <h1 className="font-display font-extrabold text-[44px] md:text-[64px] tracking-tighter leading-[0.95] mt-3">
          {is_founding ? "Founding Member." : "Welcome to Reservoir."}
        </h1>
        <p className="text-neutral-700 text-[15px] md:text-[16px] leading-relaxed mt-5 max-w-md mx-auto">
          {is_founding
            ? "You've crossed 10 referrals — lifetime Premium tier is locked in. Keep going to unlock Reservoir 25."
            : "Move up the line by inviting other points-and-miles enthusiasts. Each successful invite bumps your position."}
        </p>
      </div>

      {/* Position + referrals stats */}
      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
        <Card center>
          <div className="text-[11px] uppercase tracking-[0.16em] text-neutral-500 font-semibold">Your position</div>
          <div className="font-display font-extrabold tracking-tighter text-[56px] md:text-[72px] leading-none mt-2 tabular">
            #{position.toLocaleString()}
          </div>
          <div className="text-[12.5px] text-neutral-600 mt-2 tabular">
            of {total_waitlist.toLocaleString()} ·{" "}
            ~{estimated_weeks} {estimated_weeks === 1 ? "week" : "weeks"} at current pace
          </div>
        </Card>
        <Card center>
          <div className="text-[11px] uppercase tracking-[0.16em] text-neutral-500 font-semibold">Referrals</div>
          <div className="font-display font-extrabold tracking-tighter text-[56px] md:text-[72px] leading-none mt-2 tabular">
            {referrals}
          </div>
          <div className="text-[12.5px] text-neutral-600 mt-2">
            Current tier: <span className="font-semibold text-black">{tier?.name}</span>
          </div>
        </Card>
      </div>

      {/* Progress to next tier */}
      {next_tier ? (
        <div className="mt-6">
          <Card>
            <div className="flex items-center justify-between gap-3">
              <div className="font-display font-bold text-[14.5px]">
                {next_tier.min_refs - referrals} more to <span className="text-neutral-500">{next_tier.name}</span>
              </div>
              <div className="text-[12px] text-neutral-500 tabular">
                {referrals} / {next_tier.min_refs}
              </div>
            </div>
            <div className="mt-3 h-2 bg-black/[0.06] rounded-full overflow-hidden">
              <div
                className="h-full bg-black rounded-full transition-all duration-700"
                style={{ width: `${Math.round(progress * 100)}%` }}
                data-testid="progress-bar"
              />
            </div>
          </Card>
        </div>
      ) : null}

      {/* Share */}
      <div className="mt-10">
        <Card>
          <div className="section-eyebrow">Your referral link</div>
          <div className="mt-3 flex items-center gap-2 border border-black/10 rounded-[10px] bg-white px-3.5 py-2.5">
            <span className="flex-1 truncate text-[13px] font-mono text-neutral-700">{shareUrl}</span>
            <button
              onClick={copy}
              className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-1.5 rounded-md bg-black text-white hover:bg-neutral-800 transition-colors"
              data-testid="copy-link-btn"
            >
              {copied ? <><Check size={12}/> Copied</> : <><Copy size={12}/> Copy</>}
            </button>
          </div>

          <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="share-buttons">
            <ShareBtn href={tweet}    Icon={Twitter}        label="X" />
            <ShareBtn href={linkedin} Icon={Linkedin}       label="LinkedIn" />
            <ShareBtn href={whatsapp} Icon={MessageCircle}  label="WhatsApp" />
            <NativeShareBtn url={shareUrl} />
          </div>
        </Card>
      </div>

      {/* Tier ladder */}
      <div className="mt-10">
        <div className="section-eyebrow">Tier ladder</div>
        <div className="mt-3 card divide-y divide-black/5 overflow-hidden">
          {TIER_LADDER.map((t) => {
            const earned = referrals >= t.min_refs;
            const current = tier?.code === t.code;
            return (
              <div
                key={t.code}
                className={`flex items-center gap-4 px-5 py-3.5 ${current ? "bg-black/[0.025]" : ""}`}
                data-testid={`tier-row-${t.code}`}
              >
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${
                  earned ? "bg-emerald-500 text-white" : "bg-black/5 text-neutral-400"
                }`}>
                  {earned ? <Check size={13}/> : <span className="tabular">{t.min_refs}</span>}
                </div>
                <div className="flex-1">
                  <div className={`font-display font-bold text-[14.5px] ${earned ? "text-black" : "text-neutral-500"}`}>{t.name}</div>
                  <div className="text-[12px] text-neutral-500 mt-0.5 tabular">
                    {t.min_refs} {t.min_refs === 1 ? "referral" : "referrals"}
                  </div>
                </div>
                {current && (
                  <span className="text-[10px] uppercase tracking-[0.16em] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1">
                    You're here
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Back to landing */}
      <div className="mt-12 text-center">
        <Link to="/" className="inline-flex items-center gap-1.5 text-[13px] text-neutral-600 hover:text-black">
          Back to Reservoir <ArrowUpRight size={13}/>
        </Link>
      </div>
    </Shell>
  );
}

// ---------- Sub-components ----------------------------------------------

function Shell({ children }) {
  return (
    <div className="min-h-screen" style={{ background: "#fafaf7" }} data-testid="referral-status-page">
      <header className="border-b border-black/5 bg-white/70 backdrop-blur-xl">
        <div className="max-w-[1100px] mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-black flex items-center justify-center text-white font-bold font-display text-sm">R</div>
            <span className="font-display font-semibold tracking-tight">Reservoir <span className="text-neutral-400 font-medium">AI</span></span>
          </Link>
          <span className="text-[12px] text-neutral-500 tabular">Waitlist · 2026</span>
        </div>
      </header>
      <main className="max-w-[720px] mx-auto px-6 py-16 lg:py-24">{children}</main>
    </div>
  );
}

function Card({ children, center = false }) {
  return (
    <div className={`card bg-white rounded-[14px] border border-black/8 p-6 ${center ? "text-center" : ""}`}>
      {children}
    </div>
  );
}

function ShareBtn({ href, Icon, label }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 text-[12.5px] font-semibold px-3.5 py-2 rounded-md border border-black/10 bg-white text-neutral-800 hover:bg-black/[0.04] transition-colors"
      data-testid={`share-${label.toLowerCase()}`}
    >
      <Icon size={13} className="opacity-70" />
      {label}
    </a>
  );
}

function NativeShareBtn({ url }) {
  const [available, setAvailable] = useState(false);
  useEffect(() => {
    setAvailable(typeof navigator !== "undefined" && !!navigator.share);
  }, []);
  if (!available) return null;
  const onClick = async () => {
    try {
      await navigator.share({
        title: "Reservoir AI — join the waitlist",
        text:  "I just joined the Reservoir AI waitlist. Join with my link:",
        url,
      });
    } catch { /* user cancelled */ }
  };
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-2 text-[12.5px] font-semibold px-3.5 py-2 rounded-md border border-black/10 bg-white text-neutral-800 hover:bg-black/[0.04] transition-colors"
      data-testid="share-native"
    >
      <Share2 size={13} className="opacity-70" />
      More
    </button>
  );
}
